import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search,
  Filter,
  Building2,
  Users,
  Database,
  BookOpen,
  Globe,
  MapPin,
  Mail,
  Phone,
  ExternalLink,
  Network,
  TrendingUp,
  Award,
  GraduationCap,
  Microscope,
  Building,
  Landmark,
  HeartPulse,
  Briefcase,
} from 'lucide-react';
import { researchService } from '@/services/api/research';
import type { Institution } from '@/types/research';
import { cn } from '@/lib/utils';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

const INSTITUTION_TYPE_CONFIG: Record<Institution['type'], { icon: React.ReactNode; color: string; label: string }> = {
  university: { icon: <GraduationCap className="h-5 w-5" />, color: 'bg-blue-500', label: 'University' },
  research_center: { icon: <Microscope className="h-5 w-5" />, color: 'bg-purple-500', label: 'Research Center' },
  government: { icon: <Landmark className="h-5 w-5" />, color: 'bg-green-500', label: 'Government' },
  private: { icon: <Briefcase className="h-5 w-5" />, color: 'bg-orange-500', label: 'Private' },
  hospital: { icon: <HeartPulse className="h-5 w-5" />, color: 'bg-red-500', label: 'Hospital' },
  other: { icon: <Building className="h-5 w-5" />, color: 'bg-gray-500', label: 'Other' },
};

const COLORS = ['#667eea', '#764ba2', '#48bb78', '#ed8936', '#f687b3', '#4299e1'];

export function ResearchInstitutionsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedInstitution, setSelectedInstitution] = useState<Institution | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['research-institutions', searchQuery, typeFilter],
    queryFn: () =>
      researchService.listInstitutions({
        search: searchQuery || undefined,
        type: typeFilter !== 'all' ? (typeFilter as Institution['type']) : undefined,
      }),
  });

  const institutions = data?.data || [];

  // Sample data for institution detail
  const researchAreaData = [
    { name: 'Environmental Sciences', value: 35 },
    { name: 'Biological Sciences', value: 25 },
    { name: 'Engineering', value: 20 },
    { name: 'Physical Sciences', value: 12 },
    { name: 'Medical Sciences', value: 8 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Research Institutions</h1>
          <p className="text-muted-foreground">
            Explore partner institutions and research centers across Australia
          </p>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Institutions</p>
                <p className="text-2xl font-bold">{institutions.length * 12}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10">
                <Building2 className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Partner Organizations</p>
                <p className="text-2xl font-bold">{institutions.filter(i => i.isPartner).length * 8}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10">
                <Network className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Researchers</p>
                <p className="text-2xl font-bold">{institutions.reduce((acc, i) => acc + i.researcherCount, 0).toLocaleString()}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-500/10">
                <Users className="h-6 w-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Cross-Institutional Collabs</p>
                <p className="text-2xl font-bold">{institutions.reduce((acc, i) => acc + i.collaborationCount, 0).toLocaleString()}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10">
                <TrendingUp className="h-6 w-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search institutions by name or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[200px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(INSTITUTION_TYPE_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      {config.icon}
                      {config.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Institutions Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-12 w-12 rounded-lg mb-4" />
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {institutions.map((institution) => {
            const typeConfig = INSTITUTION_TYPE_CONFIG[institution.type];

            return (
              <Card
                key={institution.id}
                className="cursor-pointer hover:shadow-lg transition-all hover:border-primary/50 group"
                onClick={() => setSelectedInstitution(institution)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className={cn('flex h-14 w-14 items-center justify-center rounded-xl text-white', typeConfig.color)}>
                      {typeConfig.icon}
                    </div>
                    {institution.isPartner && (
                      <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
                        Partner
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg leading-tight mt-3">
                    {institution.name}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {institution.city}, {institution.state}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                    {institution.description}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t">
                    <div className="text-center">
                      <p className="text-lg font-bold text-primary">{institution.researcherCount.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Researchers</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-primary">{institution.resourceCount.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Resources</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-primary">{(institution.citationCount / 1000).toFixed(0)}K</p>
                      <p className="text-xs text-muted-foreground">Citations</p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <Badge variant="outline">
                      {typeConfig.icon}
                      <span className="ml-1">{typeConfig.label}</span>
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {institution.collaborationCount} collaborations
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Institution Detail Dialog */}
      <Dialog open={!!selectedInstitution} onOpenChange={() => setSelectedInstitution(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedInstitution && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-6">
                  <div className={cn(
                    'flex h-20 w-20 items-center justify-center rounded-xl text-white flex-shrink-0',
                    INSTITUTION_TYPE_CONFIG[selectedInstitution.type].color
                  )}>
                    {INSTITUTION_TYPE_CONFIG[selectedInstitution.type].icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <DialogTitle className="text-2xl">
                        {selectedInstitution.name}
                      </DialogTitle>
                      {selectedInstitution.isPartner && (
                        <Badge className="bg-green-500/10 text-green-600">Partner</Badge>
                      )}
                    </div>
                    <DialogDescription className="mt-1 text-base">
                      {selectedInstitution.shortName} • {INSTITUTION_TYPE_CONFIG[selectedInstitution.type].label}
                    </DialogDescription>
                    <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {selectedInstitution.city}, {selectedInstitution.state}, {selectedInstitution.country}
                      </span>
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="research">Research Areas</TabsTrigger>
                  <TabsTrigger value="contact">Contact</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6 mt-4">
                  {/* Description */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">About</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{selectedInstitution.description}</p>
                    </CardContent>
                  </Card>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <Users className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                        <p className="text-2xl font-bold">{selectedInstitution.researcherCount.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">Researchers</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <Database className="h-6 w-6 mx-auto text-green-500 mb-2" />
                        <p className="text-2xl font-bold">{selectedInstitution.resourceCount.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">Resources</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <BookOpen className="h-6 w-6 mx-auto text-purple-500 mb-2" />
                        <p className="text-2xl font-bold">{selectedInstitution.citationCount.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">Citations</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <Network className="h-6 w-6 mx-auto text-orange-500 mb-2" />
                        <p className="text-2xl font-bold">{selectedInstitution.collaborationCount}</p>
                        <p className="text-xs text-muted-foreground">Collaborations</p>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="research" className="mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Research Area Distribution</CardTitle>
                      <CardDescription>Breakdown of research focus areas</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center">
                        <div className="w-1/2 h-[250px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={researchAreaData}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={90}
                                paddingAngle={2}
                                dataKey="value"
                              >
                                {researchAreaData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: 'hsl(var(--card))',
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px',
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="w-1/2 space-y-3">
                          {researchAreaData.map((area, index) => (
                            <div key={area.name} className="flex items-center gap-3">
                              <div
                                className="h-3 w-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              />
                              <span className="text-sm flex-1">{area.name}</span>
                              <span className="text-sm font-medium">{area.value}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="contact" className="mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Contact Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {selectedInstitution.address && (
                        <div className="flex items-start gap-3">
                          <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Address</p>
                            <p className="text-sm text-muted-foreground">{selectedInstitution.address}</p>
                          </div>
                        </div>
                      )}
                      {selectedInstitution.email && (
                        <div className="flex items-start gap-3">
                          <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Email</p>
                            <a href={`mailto:${selectedInstitution.email}`} className="text-sm text-primary hover:underline">
                              {selectedInstitution.email}
                            </a>
                          </div>
                        </div>
                      )}
                      {selectedInstitution.phone && (
                        <div className="flex items-start gap-3">
                          <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Phone</p>
                            <p className="text-sm text-muted-foreground">{selectedInstitution.phone}</p>
                          </div>
                        </div>
                      )}
                      {selectedInstitution.websiteUrl && (
                        <div className="flex items-start gap-3">
                          <Globe className="h-5 w-5 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Website</p>
                            <a
                              href={selectedInstitution.websiteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline flex items-center gap-1"
                            >
                              {selectedInstitution.websiteUrl}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setSelectedInstitution(null)}>
                  Close
                </Button>
                <Button variant="outline">
                  <Users className="mr-2 h-4 w-4" />
                  View Researchers
                </Button>
                <Button>
                  <Database className="mr-2 h-4 w-4" />
                  Browse Resources
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
